import { QueryKeys } from '@shared/domain/queryKeys'
import { useApiClient } from '@shared/hooks/useApiClient'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { v4 as uuid } from 'uuid'
import type { Bet, Stage } from '../domain/Bet.domain'

interface CreateProofPointInput {
  betId: string
  stage: Stage
  text: string
  score: number
  date?: string
  ownerId?: string
}

export const useCreateProofPoint = () => {
  const api = useApiClient()
  const queryClient = useQueryClient()

  return useMutation<
    unknown,
    Error,
    CreateProofPointInput,
    { previous: Bet[] | undefined }
  >({
    mutationFn: async (data: CreateProofPointInput) => {
      const response = await api.post('/proof-points', data)
      return response.data
    },
    onMutate: async ({
      betId,
      stage,
      text,
      score,
      date = '',
    }: CreateProofPointInput) => {
      await queryClient.cancelQueries({ queryKey: [QueryKeys.Bets] })
      const previous = queryClient.getQueryData<Bet[]>([QueryKeys.Bets])

      const optimistic = {
        id: uuid(),
        text,
        score,
        date,
        ownerId: null,
        ownerName: null,
        createdAt: new Date().toISOString(),
      }
      queryClient.setQueryData<Bet[]>([QueryKeys.Bets], (old) =>
        old?.map((b) =>
          b.id === betId ? { ...b, [stage]: [...b[stage], optimistic] } : b
        )
      )

      return { previous }
    },
    onError: (_err, _vars, context) => {
      queryClient.setQueryData([QueryKeys.Bets], context?.previous)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKeys.Bets] })
    },
  })
}
